//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------
import axios, { AxiosError, AxiosResponse } from "axios";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { isString } from "lodash";
import reduce from "lodash.reduce";
import { FieldValues, UseFormSetError } from "react-hook-form";
import {
  DropDownData,
  IdNameList,
  Locale,
  OperatorFilter,
} from "../api/services/common/models";
import {
  BillingSchedule,
  SalesCreditDetail,
} from "../api/services/program/models";
import {
  ChipOptionsDataType,
  MultiOptionsDataType,
  OptionsDataType,
} from "../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadgeType,
} from "../components/statusBadge/StatusBadge";
import { TaxConfigureTableData } from "../components/taxDetails/modelsAndSchemas/Models";
import {
  CURRENCY_FORMAT_MAP,
  valueWithoutFormatInFloat,
} from "../components/textFieldCurrency/CurrencyUtils";
import { CountryField } from "../components/textFieldWithValidation/TextFieldWithValidation";
import { getNestedValue } from "../hooks/useSyncFormValidity/useSyncFormValidity";
import { FilterProps } from "../pages/clientSearch/modelsAndSchemas/Models";
import { SEARCH_TYPE_KEY } from "../pages/coachPortal/modelsAndSchemas/Models";
import {
  BillingConfigDuplicateData,
  ContractDocument,
  Country,
  ProgramTemplateDuplicateData,
  ProgramTemplateDuplicateDataDetails,
  UsedByClient,
} from "../pages/createClient/modelsAndSchemas/Models";
import { FacilitatorDetails } from "../pages/createProject/components/createProjectTabs/tabs/project/components/facilitator/model";
import { facilitatorSiteCoreKeys } from "../pages/createProject/components/createProjectTabs/tabs/project/components/facilitator/SiteCoreKeys";
import { MdmSearchActivityResultRowValue } from "../pages/mdmActivitySearch/modelsAndSchemas/models";
import { EmailNotifications } from "../pages/participants/components/modelsAndSchemas/Models";
import {
  ProjectFacilitatorTypeAssignment,
  SalesCredit,
} from "../pages/viewProject/modelsAndSchemas/models";
import { UserDetails as LoginUserDetailsType } from "../redux/modules/client/userDetails";
import { TopicListItem } from "../redux/slices/mdmActivity/models";
import {
  BE_ERROR_CODES,
  BILLING_DETAILS_SITE_CORE_VALUE,
  BRACKETS,
  CLIENT_TYPE_SITE_CORE_VALUES,
  DATE_FORMAT,
  DATE_TIME_CONSTANT,
  DOCUMENT_ORIGIN,
  DUPLICATE_DATA_VALUES,
  DuplicateDataTypes,
  EMPTY_VALUE_DASH,
  FILE_TYPE_KEYS,
  FILE_TYPES,
  FileTypeKey,
  GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES,
  MAX_LENGTH_100,
  RANGE_VALIDATION,
  REGEX_ALL_ZERO,
  REGEX_EMAIL_ACCEPTED_SPECIAL_CHARS,
  REGEX_GET_FILE_NAME,
  SYMBOL,
  TEXT_TO_REPLACE_REQUIRED_LABEL_NAME,
} from "./constants";
import { UnitTypesKey } from "./constants/siteCoreKeys";
import {
  BulkProcessCategoryEnum,
  BulkProcessCategoryLabel,
  Category,
  GroupedOption,
  KeyValue,
  LabelValue,
  MenuItemConfig,
  UserDetails,
  UserRole,
} from "./types/common";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);

// the function is created to sanitize api parameter value in order to fix veracode scan issue
export const validateApiParamValue = (value = "") => {
  if (typeof value !== "string" && typeof value !== "number") {
    throw new Error("Invalid user ID");
  }
  // Allow: a-z, A-Z, 0-9, dash (-), underscore (_), dot (.), ampersand (&), comma (,)
  return String(value).replace(/[^a-zA-Z0-9\-_.&,]/g, ""); // Sanitize
};

// Function to sanitize API parameter value to address Veracode scan issues
// Function to sanitize API parameter value allowing blank spaces
export const validateApiParamValueWithBlankSpace = (value = "") => {
  if (typeof value !== "string" && typeof value !== "number") {
    throw new Error("Invalid user ID");
  }
  // Allow: a-z, A-Z, 0-9, dash (-), underscore (_), dot (.), ampersand (&), comma (,)
  return String(value);
};

export const getInitialsFromName = (firstName: string, lastName: string) => {
  const fNameInitial = firstName.substring(0, 1).toUpperCase();
  const lNameInitial = lastName.substring(0, 1).toUpperCase();
  return `${lNameInitial}${fNameInitial}`;
};

export const isEmpty = (value: any): boolean => {
  return (
    value === undefined ||
    value === null ||
    Number.isNaN(value) ||
    (typeof value === "object" && Object.keys(value).length === 0) ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  );
};

export const isNotEmpty = <T>(value: T): value is NonNullable<T> => {
  return !isEmpty(value);
};

export const trimValue = (value: string = "") => {
  return value.trim();
};

export const mapFiltersToFormValues = (
  filters: FilterProps[],
  defaultValues: any,
  FormKey: { [key: string]: string },
) => {
  const mappedValues = { ...defaultValues };

  filters.forEach((filter) => {
    const fieldName = filter.fieldName;
    const fieldValue = filter.fieldValue;
    const operatorId = filter.operatorId;

    if (mappedValues.hasOwnProperty(`${fieldName}`)) {
      mappedValues[FormKey[fieldName as keyof typeof FormKey]] = fieldValue;
      if (typeof fieldValue === "string" && fieldValue.includes("|")) {
        mappedValues[FormKey[fieldName as keyof typeof FormKey]] =
          fieldValue.split("|");
      } else {
        mappedValues[FormKey[fieldName as keyof typeof FormKey]] = fieldValue;
      }
      if (operatorId !== "") {
        mappedValues[FormKey[`${fieldName}Filter` as keyof typeof FormKey]] =
          operatorId;
      }
    }
  });

  return mappedValues;
};

export const getYesNoString = (status: boolean) => {
  return status ? "Yes" : "No";
};

export const getUsedByClientNamesString = (
  clients: UsedByClient[],
  withComma?: boolean,
) => {
  if (!clients) {
    return "";
  }
  const separator = withComma ? "," : " ";
  return clients.map((client) => client.clientName).join(separator);
};

export const getContactTypeNamesString = (
  contactType: string[],
  withComma?: boolean,
) => {
  return contactType.join(withComma ? ", " : " ");
};

export const checkWithLowerCase = (str1: string = "", str2: string = "") =>
  str1.toLowerCase() === str2.toLowerCase();

export const isValueIncludedIgnoreCase = (
  apiValue: number | string,
  filterValue: number | string,
) => {
  if (isEmpty(apiValue) && !isEmpty(filterValue)) return false;
  if (isEmpty(apiValue) || isEmpty(filterValue)) return true;
  return apiValue
    .toString()
    .toLowerCase()
    .includes(filterValue.toString().toLowerCase());
};

export const removeExtraCharExceptPipe = (value: string = "") => {
  return value.replace(/[^|]/g, "");
};
export const removeWhiteSpace = (value: string = "") => {
  return value.replace(/\s+/g, "");
};

export const removeSpecialCharacters = (value: string) => {
  return value.replace(/[^a-zA-Z0-9〒]/g, "");
};

export const removeSpecialWithPrefix = (value: string, prefix?: string) => {
  if (prefix) {
    return removeSpecialCharacters(value.substring(prefix.length));
  }
  return removeSpecialCharacters(value);
};

export const replaceCharPattern = (pattern: string, text: string) => {
  const formattedObject = reduce(
    pattern,
    (
      acc: { formattedText: string; remainingText: string[] },
      character: string,
    ) => {
      if (acc.remainingText.length === 0) {
        return acc;
      }

      if (character !== "|") {
        return {
          formattedText: acc.formattedText + character,
          remainingText: acc.remainingText,
        };
      }

      const [head, ...tail] = acc.remainingText;

      return {
        formattedText: acc.formattedText + head,
        remainingText: tail,
      };
    },
    {
      formattedText: "",
      remainingText: text.split(""),
    },
  );
  return formattedObject;
};

export const setApiDataToDropDownOption = (
  data: DropDownData[] = [],
  isCountry = false,
  deliveryCountryName?: string, // Add this parameter to filter
) => {
  return Array.isArray(data)
    ? data
        .map((item) =>
          isCountry
            ? {
                label: item?.title ?? "",
                value: item?.key ?? "",
              }
            : {
                label: item.name,
                value: item.id,
              },
        )
        .filter((item) => item.label !== deliveryCountryName) // Remove matching label
    : [];
};

export const setApiDataToDropDownOptionForTimeZone = (
  data: DropDownData[] = [],
) => {
  if (!Array.isArray(data) || isEmpty(data)) return [];
  return data.map((item) => ({
    label: getValueOrBlank(item?.title),
    value: item.id,
  }));
};

export const setApiDataToDropDownOptionForRightOffice = (
  data: {
    rightOfficeId: string;
    rightOfficeName: string;
    countryCode: string;
  }[],
) => {
  return Array.isArray(data)
    ? data.map((item) => ({
        label: item.rightOfficeName,
        value: item.rightOfficeId,
      }))
    : [];
};

export const formatOptions = (items?: string[]): ChipOptionsDataType[] => {
  return (items || []).map((item) => ({
    value: item,
    label: item,
  }));
};

/**
 * Formats a list of UserRole objects into dropdown options, optionally filtering by groupName.
 * @param rolesList - The array of UserRole objects.
 * @param groupName - Provided, only roles with this groupName will be included.
 * @returns Array of UserRole objects for dropdowns.
 */
export const filterUserRoles = (rolesList: UserRole[], groupName: string[]) => {
  if (isEmpty(rolesList)) return [];

  const filteredRoles = rolesList.filter((role: UserRole) =>
    groupName.includes(role.groupName),
  );
  return filteredRoles;
};

export const mapTrueValues = <T extends Record<string, boolean | undefined>>(
  details: T,
  mappings: Record<keyof T, string>,
): string[] =>
  Object.entries(details)
    .filter(([_, value]) => value)
    .map(([key]) => mappings[key as keyof T]);

export const setApiDataToDropDownOptionForCountry = (
  data: Country[],
): MultiOptionsDataType[] => {
  if (!data || isEmpty(data)) return [];
  return data.map((country: Country) => ({
    label: country.countryName,
    value: country.countryId,
  }));
};

/**
 * Concatenates the first name and last name into a full name string.
 *
 * @param firstName - The first name of the person.
 * @param lastName - The last name of the person.
 * @param withComma - Whether to include a comma between the last name and first name.
 * @returns The full name string.
 */
export const getFullName = (
  firstName?: string,
  lastName?: string,
  withComma = true,
) => {
  if (firstName && lastName) {
    return `${lastName}${withComma ? "," : ""} ${firstName}`;
  }
  return firstName || lastName || "";
};

export const formatFullNameWithPrefix = (
  firstName: string,
  lastName: string,
  prefix?: string,
): string => {
  const parts = [
    prefix ? prefix.trim() : "",
    firstName.trim() + ",",
    lastName.trim(),
  ].filter(Boolean);

  return parts.join(" ").trim();
};

export const formatApiDataToDropDownOption = (
  data?: UserDetails[],
  withComma = true,
) => {
  return Array.isArray(data)
    ? data.map((item) => ({
        label: getFullName(item.firstName, item.lastName, withComma),
        value: item.id,
      }))
    : [];
};

export const formatApiDataToDropDownOptionOnlyEmail = (
  data: EmailNotifications[] = [],
) => {
  return Array.isArray(data)
    ? data.map((item) => ({
        label: item.email,
        value: item.email,
      }))
    : [];
};

export const getParsedErrorMessage = (error: any) => {
  let errorMessage = "Something went wrong";
  if (typeof error === "object" && error !== null && "isAxiosError" in error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const axiosErrorResponse = axiosError.response as AxiosResponse;
      errorMessage = axiosErrorResponse?.data[0]?.message ?? errorMessage;
    }
  }
  return errorMessage;
};

export const getParsedBEErrors = (error: any) => {
  let errors = [];
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    const axiosErrorResponse = axiosError.response as AxiosResponse;
    errors = axiosErrorResponse?.data;
  }
  return errors;
};

export const getParsedErrorCode = (error: any) => {
  const errorObj = getParsedBEErrors(error);
  if (
    isNotEmpty(errorObj) &&
    Array.isArray(errorObj) &&
    errorObj[0] !== null &&
    typeof errorObj[0] === "object" &&
    hasKey(errorObj[0], "errorCode")
  )
    return errorObj[0].errorCode ?? undefined;
};

export const isErrorCode = (error: any, expectedCode: string): boolean => {
  const errorCode = getParsedErrorCode(error);
  return errorCode && errorCode === expectedCode;
};

export const isBETimedOutError = (error: any): boolean => {
  let isBETimedOutError = false;
  if (typeof error === "object" && error !== null && "isAxiosError" in error) {
    if (
      axios.isAxiosError(error) &&
      error.response?.data?.errorMessage
        ?.toLowerCase()
        ?.includes("timeout expired")
    ) {
      isBETimedOutError = true;
    }
  }
  return isBETimedOutError;
};

export const checkIsStatusCode403Forbidden = (error: AxiosError): boolean => {
  if (typeof error === "object" && error !== null && "isAxiosError" in error) {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;
      return statusCode === BE_ERROR_CODES.FORBIDDEN_ERROR_403;
    }
  }
  return false;
};

export const reloadPage = () => {
  window.location.reload();
};

// function for replace #LABEL# this type of string with label
export const replaceHashWithValue = (
  template: string,
  values: Record<string, string> | string,
): string => {
  if (isEmpty(template)) return "";
  if (typeof values === "string") {
    return template.replace(/#(\w+)#/g, values);
  }
  return template.replace(/#(\w+)#/g, (_, key) => values[key] || "");
};

export const getVatPostalDetailsFromCountryKey = (
  countryKey: string,
  countryData?: CountryField[],
) => {
  return countryData?.find(
    (country) =>
      country.countryKey?.toLowerCase() === countryKey?.toLowerCase(),
  );
};

export const copyToClipboard = async (copyText: string) => {
  await navigator.clipboard.writeText(copyText);
};

const scrollToTopDiv = (scrollPosition = 0) => {
  const container = document.querySelector(".content-container");
  if (container) {
    container.scrollTop = scrollPosition;
  }
};

export const scrollToTopContainer = (scrollPosition?: number) => {
  setTimeout(() => {
    window.scrollTo(0, 0);
    scrollToTopDiv(scrollPosition);
  }, 500);
};
export const getDocumentStatusFromExpirationDate = (expirationDate = "") => {
  if (isNotEmpty(expirationDate)) {
    const currentDate = dayjs.utc();
    const expirationDateUTC = dayjs.utc(expirationDate).endOf("day");
    if (expirationDateUTC.isBefore(currentDate)) {
      return STATUS_BADGE_TYPE.expired;
    }
  }
  return STATUS_BADGE_TYPE.active;
};

export const getDocumentStatusFromEffectiveDate = (effectiveDate: Dayjs) => {
  if (isNotEmpty(effectiveDate)) {
    const currentDate = dayjs.utc();
    if (effectiveDate.isAfter(currentDate)) {
      return STATUS_BADGE_TYPE.awaitingEffectiveDate;
    }
  }
};

export const getGenericAccountStatusFromExpirationDate = (
  statusId: StatusBadgeType,
  expirationDate = "",
) => {
  if (isStatusExpired(statusId)) {
    return statusId;
  }
  if (isNotEmpty(expirationDate)) {
    const currentDate = dayjs.utc();
    const expirationDateUtc = dayjs.utc(expirationDate);
    if (expirationDateUtc.isBefore(currentDate)) {
      return STATUS_BADGE_TYPE.expired;
    }
  }
  return statusId;
};

export const getPOStatusFromExpirationDate = (
  statusId?: StatusBadgeType,
  expDate?: string,
) => {
  if (!statusId) return STATUS_BADGE_TYPE.active;
  const currentDate = dayjs.utc();
  const expirationDateUTC = dayjs.utc(expDate).endOf("day");
  const isExpire = expirationDateUTC.isBefore(currentDate);
  if (isExpire) {
    return STATUS_BADGE_TYPE.closed;
  }
  return statusId;
};

export const mapDropdownDataWithName = (
  data: UserDetails[] | undefined,
  emailAsValue?: boolean,
) => {
  if (!data) return [];
  const mappedData = data?.map((data) => {
    return {
      ...data,
      label: `${data.lastName}, ${data.firstName}`,
      value: emailAsValue && data?.email ? data.email : data.id,
    };
  });

  return mappedData;
};

export const mapSelectOptionToFirstIndex = (
  data: OptionsDataType[],
  selectOption: string,
) => {
  if (!Array.isArray(data)) return [];
  // Check if selectOption already exists in data
  if (data.some((option) => option.label === selectOption)) {
    return data;
  }
  return [{ label: selectOption, value: "" }, ...data];
};

export const hasKey = <T extends object>(
  obj: T,
  key: keyof any,
): key is keyof T => {
  return key in obj;
};

export const hasCountryNameKey = (countries: any[]) => {
  return countries.some(
    (country) => typeof country === "object" && "countryName" in country,
  );
};
export const getCountryIdsAsStringArray = (
  countries: Country[] | MultiOptionsDataType[] = [],
): string[] => {
  return countries.map((country: Country | MultiOptionsDataType) =>
    "countryId" in country ? country.countryId : (country.value as string),
  );
};

export const getIdsAsStringArray = (
  data: Partial<IdNameList>[] | undefined,
): string[] => {
  if (!data) return [];

  return data
    .map((item) => ("id" in item ? item.id : ""))
    .filter((id): id is string => id !== undefined && id !== "");
};

export const convertToUTCFormat = (
  dateValue?: string | null,
  userCountryDateFormat?: string,
  returnExpirationTime = false, // Set the time to 23:59:59 of the given date
) => {
  if (!dateValue) return "";
  if (returnExpirationTime) {
    const formattedDate = dayjs(dateValue, userCountryDateFormat)
      .endOf("day") // Set the time to 23:59:59 in the local timezone
      .utc(true) // Convert to UTC, keeping the same time (without any shifting)
      .format(); // Output the date in ISO 8601 format
    return formattedDate;
  }
  // Parse the date, set it to midnight in local time, then convert it to UTC
  const formattedDate = dayjs(dateValue, userCountryDateFormat)
    .startOf("day") // Set the time to 00:00:00 in the local timezone
    .utc(true) // Convert to UTC, keeping the same time (without any shifting)
    .format(); // Output the date in ISO 8601 format
  return formattedDate;
};

export const convertToUTCFormatWithTime = (
  dateValue: string,
  userCountryDateFormat?: string,
) => {
  if (!dateValue) return "";

  return dayjs(dateValue, userCountryDateFormat).utc(false).format();
};

export const formatWithTimezone = (
  fromDate: string | Date,
  tz: string,
  format: string,
) => {
  return dayjs(fromDate).tz(tz).format(format);
};

//function to covert utc time to given timezone
export const convertUTCToTimeZone = (
  utcDateTime: string,
  timeZone: string,
  formatStr: string,
) => {
  if (!utcDateTime) return "";

  return dayjs.utc(utcDateTime).tz(timeZone).format(formatStr);
};

// to pass the date and time in api with selected timezone
export const covertSelectedTimezoneWithUtc = (
  timeZone: string,
  selectedDateTime: string,
  format?: string,
) => {
  if (!selectedDateTime) return "";

  let localTime;

  // Check if it's already a Day.js object or valid ISO string
  if (typeof selectedDateTime === "object" || selectedDateTime.includes("T")) {
    localTime = dayjs(selectedDateTime);
  } else {
    // Try parsing as user format first (DD/MM/YYYY hh:mm A)
    const userFormatDate = dayjs(selectedDateTime, format, true);

    if (userFormatDate.isValid()) {
      localTime = userFormatDate;
    } else {
      localTime = dayjs(selectedDateTime);
    }
  }

  // Change it to the target timezone
  const selectedTimeZoneFormat = dayjs.tz(
    localTime.format(DATE_FORMAT.LOCAL_FORMAT),
    timeZone,
  );

  return selectedTimeZoneFormat.utc().format(DATE_FORMAT.UTC_FORMAT);
};

export const convertToUserFormat = (
  utcDate?: string,
  targetFormat?: string,
  timeZone?: string,
) => {
  if (!utcDate) return "";
  // Parse the UTC date, then format it to the target format
  const formattedDate = dayjs.utc(utcDate).format(targetFormat); // Format to the target format
  if (timeZone) {
    return `${formattedDate} ${getFormattedTimeZone(timeZone)}`;
  }
  return formattedDate;
};

export const convertToUserFormatWithLocalTime = (
  utcDate?: string,
  targetFormat?: string,
  timeZone?: string,
) => {
  if (!utcDate) return "";
  // Parse the UTC date, convert it to local time, then format it to the target format
  const formattedDate = dayjs.utc(utcDate).local().format(targetFormat); // Format to the target format
  if (timeZone) {
    return `${formattedDate} ${getFormattedTimeZone(timeZone)}`;
  }
  return formattedDate;
};

export const getFormattedTime = (
  utcDate?: string,
  targetFormat: string = DATE_FORMAT.TIME_HOUR_MINUTE_12H,
): string => {
  // Convert to user's local timezone & format as 12-hour clock with AM/PM
  return dayjs.utc(utcDate).local().format(targetFormat);
};

export const getFormattedTimeZone = (timeZone?: string) => {
  return timeZone
    ? `${BRACKETS.LEFT_SQUARE}${timeZone.toUpperCase()}${BRACKETS.RIGHT_SQUARE}`
    : "";
};

export const getFromToTimeDuration = (
  startTime: string,
  endTime: string,
  timeZone?: string,
) => {
  const formattedStartTime = getFormattedTime(startTime);
  const formattedEndTime = getFormattedTime(endTime);

  if (!formattedStartTime || !formattedEndTime) return "";
  return [
    formattedStartTime,
    SYMBOL.MINUS,
    formattedEndTime,
    getFormattedTimeZone(timeZone),
  ].join(" ");
};

export const formatSeconds = (seconds: number): string => {
  if (seconds === 0) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}${DATE_TIME_CONSTANT.HOUR} ${minutes.toString().padStart(2, "0")}${DATE_TIME_CONSTANT.MINUTE}`;
};

export const getCurrencyNameAndLocaleById = (
  currencyList: IdNameList[],
  currencyTypeId: string | undefined = "",
) => {
  const currencyNameById = getNameByIdFromList(currencyList, currencyTypeId);
  const currencyFormat = CURRENCY_FORMAT_MAP[currencyNameById];

  return {
    currencyName: currencyNameById,
    currencyLocale: currencyFormat?.locale,
  };
};

export const getFormattedAmount = (
  value: string | number,
  locale = "en-US",
) => {
  const numValue = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return value;
  }

  return new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

export const calculateTotalTaxAmount = (
  filteredResults: TaxConfigureTableData[],
) => {
  let totalAmountOrPercentage = 0;
  filteredResults.forEach((item) => {
    totalAmountOrPercentage += valueWithoutFormatInFloat(item.amount);
  });
  return totalAmountOrPercentage;
};

export const filterEmptyParams = (params: any) => {
  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const value = params[key];

      // Check for empty values: empty string, null, undefined, or empty array/object
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "object" && Object.keys(value).length === 0)
      ) {
        delete params[key];
      } else if (typeof value === "object") {
        filterEmptyParams(value); // Recurse if it's an object
      }
    }
  }
  return params;
};

export const compareDates = (
  inputDate: Date | string,
  rowDate: string,
  format: string,
) => {
  if (!inputDate) return true;
  const itemDate = dayjs.utc(rowDate).format(format);
  const comparisonDate =
    typeof inputDate === "string"
      ? dayjs(inputDate, format).format(format)
      : dayjs(inputDate).format(format);
  return itemDate === comparisonDate;
};

const COUNTRY_PREFIX = "country.";
const normalizeObject = <T extends Record<string, any>>(
  obj: T,
  excludeKeys: (keyof T)[] = [], // Keys to exclude (like 'id')
  type?: DuplicateDataTypes,
) => {
  const filteredObj = Object.fromEntries(
    Object.entries(obj)
      .filter(([key]) => !excludeKeys.includes(key as keyof T)) // Exclude specified keys
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [
            key,
            value
              .map((item) =>
                isString(item)
                  ? item.toLowerCase() // Convert to lowercase
                  : item,
              )
              .sort(),
          ];
        } else if (isString(value)) {
          return [key, value.toLowerCase()]; // Convert standalone strings to lowercase
        }
        return [key, value]; // Keep other types unchanged
      }),
  );

  if (type === DUPLICATE_DATA_VALUES.CLIENT_SPECIFIC_PROGRAM_TEMPLATE) {
    filteredObj.countries = (filteredObj.countries as string[] | undefined)
      ?.map((country) =>
        getCountryCodeWithoutCountryPrefix(country).toLowerCase(),
      ) // Remove "country." prefix
      .sort();
  }

  // Sort keys before stringifying to ensure consistent ordering
  const sortedObj = Object.fromEntries(
    Object.entries(filteredObj).sort(([a], [b]) => a.localeCompare(b)),
  );

  return JSON.stringify(sortedObj); // Convert object to string;
};

export const findFirstDuplicateBillingConfigName = <
  T extends Record<string, any>,
>(
  data: T[],
): BillingConfigDuplicateData | undefined => {
  if (!Array.isArray(data) || data.length === 0) {
    return undefined;
  }

  const nameMap = new Map<string, number>();

  for (let i = 0; i < data.length; i++) {
    const item = data[i];

    if (!item?.billingConfigurationName) {
      continue;
    }

    const configName =
      typeof item.billingConfigurationName === "string"
        ? item.billingConfigurationName.toLowerCase()
        : String(item.billingConfigurationName).toLowerCase();

    if (nameMap.has(configName)) {
      return {
        type: DUPLICATE_DATA_VALUES.BILLING_CONFIGURATION,
        parentIndex: null,
        duplicateIndexes: [nameMap.get(configName)!, i],
      };
    }

    nameMap.set(configName, i);
  }

  return undefined;
};

export const findFirstDuplicateData = <T extends Record<string, any>>(
  data: T[],
  type: DuplicateDataTypes,
  nestedKey?: keyof T,
  excludeKeys: string[] = [],
): BillingConfigDuplicateData | undefined => {
  if (nestedKey) {
    const nestedResult = findNestedDuplicates(
      data,
      nestedKey,
      excludeKeys,
      type,
    );
    if (nestedResult) return nestedResult;
  }

  return findTopLevelDuplicates(data, excludeKeys, type);
};

const findNestedDuplicates = <T extends Record<string, any>>(
  data: T[],
  nestedKey: keyof T,
  excludeKeys: string[],
  type: DuplicateDataTypes,
) => {
  for (let index = 0; index < data.length; index++) {
    const nestedArray = data[index][nestedKey] as unknown as T[];

    if (Array.isArray(nestedArray) && nestedArray.length > 1) {
      const duplicate = checkDuplicatesInArray(nestedArray, excludeKeys);
      if (duplicate) {
        return { type, parentIndex: index, duplicateIndexes: duplicate };
      }
    }
  }
  return null;
};

const checkDuplicatesInArray = <T extends Record<string, any>>(
  nestedArray: T[],
  excludeKeys: string[],
) => {
  const nestedMap = new Map<string, number>();

  for (let nestedIndex = 0; nestedIndex < nestedArray.length; nestedIndex++) {
    const normalizedNestedItem = normalizeObject(
      nestedArray[nestedIndex],
      excludeKeys,
    );

    if (nestedMap.has(normalizedNestedItem)) {
      return [nestedMap.get(normalizedNestedItem)!, nestedIndex];
    } else {
      nestedMap.set(normalizedNestedItem, nestedIndex);
    }
  }
  return null;
};

const findTopLevelDuplicates = <T extends Record<string, any>>(
  data: T[],
  excludeKeys: string[],
  type: DuplicateDataTypes,
) => {
  const configMap = new Map<string, number>(); // Map to store config string → first occurrence index

  for (let index = 0; index < data.length; index++) {
    const item = data[index];
    const configString = normalizeObject(item, excludeKeys, type); // Convert config object to string

    if (configMap.has(configString)) {
      return {
        type,
        parentIndex: null,
        duplicateIndexes: [configMap.get(configString)!, index],
      }; // Return first duplicate pair
    } else {
      configMap.set(configString, index); // Store the first occurrence index
    }
  }
  return undefined;
};

export const findDuplicateProgramTemplateData = <T extends Record<string, any>>(
  data: T[],
  type: DuplicateDataTypes,
  programTemplateKey = "programTemplateConfigurations",
): ProgramTemplateDuplicateData | undefined => {
  if (!programTemplateKey) return undefined;

  const seenTemplates = new Map<string, ProgramTemplateDuplicateDataDetails>();

  for (let index = 0; index < data.length; index++) {
    const programTemplateItems = data[index][
      programTemplateKey
    ] as unknown as T[];

    if (
      !Array.isArray(programTemplateItems) ||
      programTemplateItems.length === 0
    ) {
      continue;
    }

    for (
      let nestedIndex = 0;
      nestedIndex < programTemplateItems.length;
      nestedIndex++
    ) {
      const programTemplateId =
        programTemplateItems[nestedIndex]?.programTemplateId;

      if (!programTemplateId) continue;

      const existingEntry = seenTemplates.get(programTemplateId);

      if (existingEntry) {
        return {
          type,
          duplicateDataDetails: [
            existingEntry,
            {
              programTemplateId,
              programTemplateIndex: nestedIndex,
              parentIndex: index,
            },
          ],
        };
      }

      seenTemplates.set(programTemplateId, {
        programTemplateId,
        programTemplateIndex: nestedIndex,
        parentIndex: index,
      });
    }
  }

  return undefined;
};

export const calculateAge = (
  birthDate: string,
  userCountryDateFormat: string,
) => {
  const birthDay = dayjs(birthDate, userCountryDateFormat);
  const currentDay = dayjs();

  const age = currentDay.diff(birthDay, "years");

  return age;
};

export const getNameByIdFromList = (
  dataList: IdNameList[] = [],
  id: string = "",
) => {
  return dataList.find((item) => item.id === id)?.name ?? "";
};

export const getIdByNameFromList = (
  dataList: IdNameList[] = [],
  name: string = "",
) => {
  return dataList.find((item) => item.name === name)?.id ?? "";
};

export const isValueEmptyOrDash = (value: string) => {
  return isEmpty(value) || value === EMPTY_VALUE_DASH;
};

export const getValueOrDash = (value?: string): string => {
  return isEmpty(value) ? EMPTY_VALUE_DASH : value!;
};

export const getValueOrBlank = (
  value?: string | null,
  fallbackValue: string = "",
) => {
  return value ?? fallbackValue;
};

export const getValueOrBlankArray = <T>(value?: T[]) => {
  return value ?? [];
};

export const getNumberValueAsStringOrBlank = (
  value?: number,
  fallbackValue: string = "",
) => {
  return value !== undefined ? value.toString() : fallbackValue;
};

export const getBooleanValueOrFallback = (
  value: boolean = false,
  fallbackValue: boolean = false,
) => {
  return value ?? fallbackValue;
};

export const getValueOrZero = (value?: number) => {
  return value ?? 0;
};

export const getFullNameById = (
  id: string,
  data: { firstName: string; id: string; lastName: string }[] | undefined,
) => {
  if (!data) return EMPTY_VALUE_DASH;
  if (isEmpty(id)) return EMPTY_VALUE_DASH;
  if (isEmpty(data)) return EMPTY_VALUE_DASH;
  const person = data.find((item) => item.id === id);
  return person
    ? getFullName(person?.firstName, person?.lastName)
    : EMPTY_VALUE_DASH;
};

export const getCountryTitleByKey = (
  key: string,
  countryListData: { key: string; title: string }[],
) => {
  if (!countryListData) return EMPTY_VALUE_DASH;
  const country = countryListData.find(
    (country: { key: string; title: string }) => country.key === key,
  );
  return country ? country.title : EMPTY_VALUE_DASH;
};

export const getProgramTemplateById = (
  id: string,
  templates: { id: string; name: string }[],
) => {
  if (!templates) return EMPTY_VALUE_DASH;
  const template = templates.find((template) => template.id === id);
  return template ? template.name : EMPTY_VALUE_DASH;
};

export const formatDate = (
  date: string | undefined,
  userCountryDateFormat: string | undefined,
): string => {
  return date ? (convertToUserFormat(date, userCountryDateFormat) ?? "") : "";
};

export const setTopicApiDataToDropDownOption = (
  data: { topic: string; id: string }[],
) => {
  return Array.isArray(data)
    ? data.map((item) => ({
        label: item.topic,
        value: item.id,
      }))
    : [];
};

export const checkValueIsZero = (value: string = "0") => {
  const regZeroCheck = REGEX_ALL_ZERO;
  regZeroCheck.lastIndex = 0;
  return regZeroCheck.test(removeSpecialWithPrefix(value.toString()));
};

export const trimParams = (params: any) => {
  if (typeof params !== "object" || params === null) {
    return params;
  }

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const value = params[key];

      if (typeof value === "string") {
        params[key] = value.trim();
      } else if (Array.isArray(value)) {
        params[key] = value.map((item) => trimParams(item));
      } else if (typeof value === "object") {
        params[key] = trimParams(value);
      }
    }
  }

  return params;
};

export const checkPercentageRangeError = (value: string) => {
  const numberValue = Number(value || 0);
  if (
    numberValue < RANGE_VALIDATION.ONE ||
    numberValue > RANGE_VALIDATION.HUNDRED
  )
    return true;

  return false;
};

export const isSolutionOfferingAllowedForTransfer = (
  listOfSolutionOfferings: string[],
  solutionOffering: string,
) => {
  return !isEmpty(solutionOffering)
    ? listOfSolutionOfferings.includes(solutionOffering)
    : false;
};

export const isTestAccess = (value: string) => {
  return checkWithLowerCase(
    value,
    GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES.TEST_ACCESS,
  );
};

export const isPurposeClient = (value: string) => {
  return checkWithLowerCase(
    value,
    GENERIC_ACCOUNT_PURPOSE_LIST_SITE_CORE_VALUES.CLIENT,
  );
};

export const isRetailClient = (clientTypeId: string | undefined) => {
  return checkWithLowerCase(
    clientTypeId,
    CLIENT_TYPE_SITE_CORE_VALUES.RETAIL_CLIENT,
  );
};

export const isAffiliateClient = (clientTypeId: string | undefined) => {
  return checkWithLowerCase(
    clientTypeId,
    CLIENT_TYPE_SITE_CORE_VALUES.AFFILIATE_CLIENT,
  );
};

export const isDateInRange = (
  date: dayjs.Dayjs,
  fromDate: dayjs.Dayjs,
  toDate: dayjs.Dayjs,
) => {
  return (
    date.isSameOrAfter(fromDate, "day") && date.isSameOrBefore(toDate, "day")
  );
};

export const getSeriesIndexFromScheduleMilestone = (value: string) =>
  Number(value.split(" ")[1]);

export const getValueWith2DecimalDigits = (value: string | null = "0") =>
  Number(value).toFixed(2);

export const getFieldValue = (
  data: string | undefined,
  fallback = "",
): string => {
  return data ?? fallback;
};

export const getAmountWithFormatByCurrencyName = (
  amount: number,
  currencyName: string,
) => {
  return `${getFormattedAmount(
    amount,
    CURRENCY_FORMAT_MAP[currencyName]?.locale,
  )}`;
};

export const getValueByPercentage = (
  value: string | number = 0,
  percentageValue: string | number = 0,
) =>
  Math.round(
    Number(value) * (Number(percentageValue) / 100 + Number.EPSILON) * 100,
  ) / 100; // round to 2 decimal place

/**
 * Filter list base on filter object
 * generic method
 * @param list
 * @param filters
 * @returns
 */

// Helper function to check if a string is a date/datetime
const isDateTimeString = (str: string): boolean => {
  // ISO 8601 date format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS
  const dateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?/;
  return dateRegex.test(str);
};

// Helper function to extract date in standard YYYY-MM-DD format
const extractStandardDateFormat = (dateTimeString: string): string => {
  // Handles ISO format: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS or with timezone
  const dateMatch = dateTimeString.match(/^\d{4}-\d{2}-\d{2}/);
  return dateMatch ? dateMatch[0] : dateTimeString;
};

// Helper function to normalize strings for comparison
const normalizeStringForComparison = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[\[\](){}]/g, "") // Remove brackets and parentheses
    .replace(/[-_.]/g, " ") // Replace hyphens, underscores, dots with spaces
    .replace(/\s+/g, " ") // Normalize multiple spaces to single space
    .trim();
};

/**
 * Generic filter list based on filter object with intelligent string matching
 * Handles semantic equivalence for strings (e.g., "Billing-Other" matches "billing [other]")
 * Also handles date/datetime comparisons by extracting and comparing date portions only
 * Works with dates in any format (country-specific or ISO format)
 *
 * @param list - Array of objects to filter
 * @param filters - Filter criteria object (dates should be in YYYY-MM-DD format for consistency)
 * @returns Filtered list matching all filter criteria
 */
export const filterListByFilterKeyValueAdvanced = (
  list: KeyValue[],
  filters: KeyValue,
) => {
  return list.filter((item) => {
    return Object.keys(filters).every((key) => {
      const filterValue = filters[key];
      const orderValue = item[key];

      if (!filterValue) return true; // Skip filters with empty values
      const filterString = String(filterValue);
      const orderString = String(orderValue);
      if (key?.toLowerCase().includes("date")) {
        // Special handling for date/datetime strings
        // Both ISO format dates (from API) and country format dates (from user input)
        if (isDateTimeString(orderString)) {
          // orderValue is likely ISO format (from API)
          const orderDateStandard = extractStandardDateFormat(orderString);

          // filterValue could be in any format, but we extract YYYY-MM-DD if present
          const filterDateStandard = extractStandardDateFormat(filterString);

          return orderDateStandard.includes(filterDateStandard);
        }
      }

      // For non-date strings, use semantic matching
      const normalizedFilterValue = normalizeStringForComparison(filterString);
      const normalizedOrderValue = normalizeStringForComparison(orderString);

      return normalizedOrderValue.includes(normalizedFilterValue);
    });
  });
};

export const filterListByFilterKeyValue = (
  list: KeyValue[],
  filters: KeyValue,
) => {
  return list.filter((item) => {
    return Object.keys(filters).every((key) => {
      const filterValue = filters[key];
      const orderValue = item[key];
      if (!filterValue) return true; // Skip filters with empty values
      const filterValueLower = String(filterValue).toLowerCase();
      const orderValueLower = String(orderValue).toLowerCase();
      return orderValueLower?.toString().includes(filterValueLower);
    });
  });
};

export const getPropertyFromArray = (input: [], field: string) => {
  let output: string[] = [];
  for (const item of input) output.push(item[field]);
  return output;
};

export const isDocumentInherited = (source: string) =>
  source === DOCUMENT_ORIGIN.inherited;

// helper functions to check the status value
export const isStatusActive = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.active;
export const isStatusInActive = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.inActive;
export const isStatusExpired = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.expired;
export const isStatusExecuted = (status: string) =>
  status === STATUS_BADGE_TYPE.executed;
export const isStatusInvoiced = (status: string) =>
  status === STATUS_BADGE_TYPE.invoiced;
export const isStatusOpen = (status: string) =>
  status === STATUS_BADGE_TYPE.open;
export const isStatusClosed = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.closed;
export const isStatusAutoRenewed = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.autoRenewed;
export const isStatusConcon = (status: StatusBadgeType) =>
  status === STATUS_BADGE_TYPE.conCon;

export const checkFilterMatch = (inputValue: string, rowItem: string) => {
  return isEmpty(inputValue?.toLowerCase())
    ? true
    : !isEmpty(rowItem) &&
        rowItem?.toLowerCase().includes(inputValue?.toLowerCase());
};

export const formatDecimalWithPrecision = (
  value: string,
  allowDigit: number,
): string => {
  const [numberValue, digitValue] = value.split(".");
  if (digitValue?.length > allowDigit) {
    return `${numberValue}.${digitValue.slice(0, allowDigit)}`;
  }
  return value;
};

export const arrayOfObjectsIncludesValue = <T extends Record<string, any>>(
  inputValue: string,
  rowList: T[],
  key: keyof T,
): boolean => {
  if (isEmpty(inputValue)) return true;

  const lowerInput = inputValue.toLowerCase();
  return (
    rowList?.some((item) =>
      String(item[key])?.toLowerCase().includes(lowerInput),
    ) ?? false
  );
};

export const arrayOfStringsIncludesValue = (
  inputValue: string,
  rowList: string[],
): boolean => {
  if (isEmpty(inputValue)) return true;

  const lowerInput = inputValue.toLowerCase();
  return (
    rowList?.some((item) => item?.toLowerCase().includes(lowerInput)) ?? false
  );
};

export const stringIncludesArrayOfStringsValue = (
  inputValues: string[],
  rowValue: string,
): boolean => {
  if (isEmpty(inputValues)) return true;

  const lowerRowValue = rowValue.toLowerCase();
  return inputValues.some((value) =>
    lowerRowValue.includes(value?.toLowerCase()),
  );
};

export const getCurrencyFormat = (currencyName: string) => {
  return CURRENCY_FORMAT_MAP[currencyName];
};

export const getAmountWithCurrency = (amount: number, currencyName: string) => {
  const currencyFormat = getCurrencyFormat(currencyName) ?? "";
  return amount !== undefined && amount !== null
    ? `${currencyName} ${getFormattedAmount(amount, currencyFormat?.locale)}`
    : EMPTY_VALUE_DASH;
};

export const isDuplicateTopic = (
  topicList: TopicListItem[],
  topicValue: string | undefined | null,
  excludeId?: string,
): boolean => {
  if (!topicValue) return false;

  const trimmedValue = topicValue.trim().toLowerCase();

  return topicList.some(
    (t) =>
      t.topic.trim().toLowerCase() === trimmedValue &&
      (!excludeId || t.id !== excludeId), // Exclude current editing topic
  );
};

export const getHelperText = (helperText: string, label: string): string => {
  if (helperText?.includes(TEXT_TO_REPLACE_REQUIRED_LABEL_NAME)) {
    return replaceHashWithValue(helperText, label);
  }
  return helperText;
};

export const getUniqueFilters = <T extends Record<string, any>>(
  data: T[],
  key: keyof T,
  labelKey?: keyof T,
): LabelValue[] => {
  const seen = new Map<string, string>();

  data.forEach((item) => {
    const value = item[key];
    const label = labelKey ? item[labelKey] : value;

    if (value && typeof value === "string" && !seen.has(value)) {
      seen.set(value, String(label));
    }
  });

  return Array.from(seen, ([value, label]) => ({ value, label }));
};

export const getUniqueCountriesFromData = <T extends Record<string, any>>(
  data: T[],
) => {
  const uniqueValues = Array.from(
    new Map(
      data
        .filter(
          (item) => isNotEmpty(item?.country) && isNotEmpty(item?.countryName),
        )
        .map((item) => [
          item.country, // use this as the unique key
          { value: item.country, label: item.countryName ?? "" },
        ]),
    ).values(),
  );

  return uniqueValues;
};

export const getUniqueLabelOptions = <T, K extends keyof T>(
  data: T[],
  key: K,
): { value: string; label: string }[] =>
  Array.from(
    new Set(
      data
        .flatMap((item) => {
          const val = item[key];
          return Array.isArray(val) ? val : [val];
        })
        .filter((label): label is string => Boolean(label)),
    ),
  ).map((label) => ({
    value: label,
    label,
  }));

export const createDropdownLabelResolver =
  (
    dropdownKey: string,
    listKey: string,
    resolver: (dropdownKey: string, listKey: string, item: string) => string,
  ) =>
  (value: string[] | undefined): string[] | undefined =>
    Array.isArray(value)
      ? value.map((item) => resolver(dropdownKey, listKey, item))
      : undefined;

export const getFieldUndefinedForPayloadParams = <
  T extends Record<string, any>,
>(
  obj: T,
): Partial<T> => {
  const values = Object.values(obj);
  const hasNested = values.some(
    (v) => typeof v === "object" && v !== null && !Array.isArray(v),
  );

  // Fast path: no nested objects (common case)
  if (!hasNested) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, value]) => isNotEmpty(value)),
    ) as Partial<T>;
  }

  // Has nested: process recursively
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (!isNotEmpty(value)) continue;

    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      result[key] = value;
    } else {
      const nested = getFieldUndefinedForPayloadParams(value);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
    }
  }

  return result as Partial<T>;
};

export const isMeasurementMinutes = (unit: string) => {
  return unit === UnitTypesKey.Minutes;
};

export const getFileTypeMap = (
  ...keys: FileTypeKey[]
): Record<string, string[]> =>
  keys.reduce(
    (result, key) => {
      const { type, extensions } = FILE_TYPES[key];
      result[type] = result[type]
        ? [...new Set([...result[type], ...extensions])]
        : [...extensions];
      return result;
    },
    {} as Record<string, string[]>,
  );

export const getDefaultFileTypes = (): Record<string, string[]> =>
  getFileTypeMap(...FILE_TYPE_KEYS.DEFAULT);

export const getAllFileTypes = (): Record<string, string[]> =>
  getFileTypeMap(...(Object.keys(FILE_TYPES) as FileTypeKey[]));

export const isMyCaseLoads = (value: string) => {
  return checkWithLowerCase(value, SEARCH_TYPE_KEY.myCaseLoads);
};

export const isAllParticipants = (value: string) => {
  return checkWithLowerCase(value, SEARCH_TYPE_KEY.allParticipants);
};

export const getExpectation = (
  unit: string,
  measurement: string,
  siteCoreFunction: (value: string) => string,
) => {
  return unit === UnitTypesKey.Minutes
    ? Number(siteCoreFunction(measurement))
    : Number(measurement);
};

export const setApiDataToDropDownOptionForRole = (
  data: UserRole[],
): MultiOptionsDataType[] => {
  if (!data || isEmpty(data)) return [];
  return data.map((role: UserRole) => ({
    label: role.role,
    value: role.roleId,
  }));
};

export const convertArrayTostring = (array: string[]) => {
  if (!Array.isArray(array)) return "";
  return array.join(",");
};

export const setApiDataToDropDownOptionForActivityType = (
  data: MdmSearchActivityResultRowValue[],
): MultiOptionsDataType[] => {
  if (!data || isEmpty(data)) return [];
  return data.map((activity: MdmSearchActivityResultRowValue) => ({
    label: activity.activityType,
    value: activity.id,
  }));
};

export const convertStringArrayToOptions = (data: string[]) => {
  return data.map((item: string) => ({
    label: item,
    value: item,
  }));
};

export const extractValue = (key?: string) => key?.split(".").pop() ?? "";

export const getFormattedSalary = (
  currencyName?: string,
  salaryRange?: string,
) => {
  // Convert "35K-70K" to "35K to 70K"
  const formattedRange = salaryRange ? salaryRange.replace("-", " to ") : "";

  if (!currencyName && !formattedRange) return "--"; // If both are empty

  return formattedRange ? `${currencyName} ${formattedRange}`.trim() : "--";
};

export const extractFileName = (contentDisposition: string) => {
  const regex = REGEX_GET_FILE_NAME;
  const result = regex.exec(contentDisposition);
  if (result?.[1]) {
    return result[1].replace(/['"]/g, "");
  }
  return "";
};

export const toCamelCaseFromTitle = (string: string) => {
  if (isEmpty(string)) return;
  return string
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0
        ? lower // first word in lowercase
        : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join("");
};

/**
 * Returns the start of tomorrow's date
 */
export const getTomorrowDate = () => {
  return dayjs().add(1, "day").startOf("day");
};

export const getTodayDate = () => {
  return dayjs().startOf("day");
};

export const getMaxAllowedPastDate = () => {
  return dayjs().endOf("day");
};

/**
 * Returns the start of yesterday's date
 */
export const getYesterdayDate = () => {
  return dayjs().subtract(1, "day").startOf("day");
};

export const getProcessCategoryEnum = (
  category: Category,
): BulkProcessCategoryEnum => {
  const label = category?.categoryName;

  switch (label) {
    case BulkProcessCategoryLabel.EDIT:
      return BulkProcessCategoryEnum.EDIT;
    case BulkProcessCategoryLabel.CREATE:
      return BulkProcessCategoryEnum.CREATE;
    case BulkProcessCategoryLabel.INSERT_ACTIVITIES:
      return BulkProcessCategoryEnum.INSERT_ACTIVITIES;
    case BulkProcessCategoryLabel.TRANSFER_PARTICIPANTS:
      return BulkProcessCategoryEnum.TRANSFER_PARTICIPANTS;
    default:
      return BulkProcessCategoryEnum.CREATE;
  }
};

// to convert Route URI (with id's) TO match the siteCore URL (Without id's)
export const getMatchedPageName = (
  pathname: string,
  routeMap: Record<string, any>,
) => {
  return Object.keys(routeMap).find((route) => {
    const dynamicRoute = route.replace(/:[^/]+/g, "[^/]+"); // Convert `:id` to regex
    const regex = new RegExp(`^${dynamicRoute}$`);
    return regex.test(pathname);
  });
};

export const buildActionMenuItems = <RowProps>(
  row: RowProps,
  configs: MenuItemConfig<RowProps>[],
  hasModulePermission: (key: string) => boolean,
) => {
  const menuItems = configs
    .filter((config) => {
      const passesPermission = config.permissionKey
        ? hasModulePermission(config.permissionKey)
        : true;
      const passesShow = config.show ? config.show(row) : true;
      return passesPermission && passesShow;
    })
    .map((config) => config.create(row));

  return menuItems.length > 0 ? menuItems : null;
};
export const createOptionsFromSet = <T extends string>(
  set: Set<T>,
  labelFetcher?: (val: T) => string,
) =>
  Array.from(set).map((value) => ({
    label: labelFetcher ? labelFetcher(value) : value,
    value,
  }));

export const getFormArrayInstanceHasErrors = (
  errors: Record<string, any>,
  index: number,
  formKey: string,
  requiredFields: string[],
): boolean => {
  const paths = requiredFields.map((field) => `${formKey}[${index}].${field}`);
  return !paths.some((path) => !!getNestedValue(errors, path));
};

export const isActiveStatusAndImmediateBillingType = (
  programStatus: StatusBadgeType,
  billingType: string,
) => {
  const isImmediateBillingType = isImmediateBilling(billingType);

  return isStatusActive(programStatus) && isImmediateBillingType;
};
export const isOpenStatusAndImmediateBillingType = (
  programStatus: StatusBadgeType,
  billingType: string,
) => {
  const isImmediateBillingType = isImmediateBilling(billingType);

  return isStatusOpen(programStatus) && isImmediateBillingType;
};

export const getBillingScheduleExecutionStatus = (
  schedules: BillingSchedule[],
) => {
  if (isEmpty(schedules)) return { allExecuted: false, hasOneExecuted: false };

  const allExecuted = schedules.every(
    (schedule) => schedule.statusId === STATUS_BADGE_TYPE.executed,
  );
  const hasOneExecuted = schedules.some(
    (schedule) => schedule.statusId === STATUS_BADGE_TYPE.executed,
  );
  return { allExecuted, hasOneExecuted };
};

export const isBilledAndActivityBasedBillingType = (
  billingType: string,
  isBilled?: boolean,
) => {
  const isActivityBasedBillingType =
    billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity;

  return isBilled && isActivityBasedBillingType;
};

export const isImmediateBilling = (billingType: string) =>
  billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeImmediateBilling;

export const isMilestoneBilling = (billingType: string) =>
  billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone;

export const isActivityBasedBilling = (billingType: string) =>
  billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeActivity;

export const isScheduledBasedBilling = (billingType: string) =>
  billingType === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule;

export const isBillingTypeMilestoneOrSchedule = (billingType: string) =>
  isMilestoneBilling(billingType) || isScheduledBasedBilling(billingType);

export function getAriaRequiredStatus(
  isMandatory?: boolean,
  disabled?: boolean,
): boolean {
  return !!isMandatory && !disabled;
}

export function getAriaLabelText(
  label?: React.ReactNode,
  isMandatory?: boolean,
  disabled?: boolean,
  requiredARIALabel?: string,
): string {
  return `${typeof label === "string" ? label : ""} ${
    isMandatory && !disabled ? requiredARIALabel : ""
  }`.trim();
}

export const getValueByKey = (
  list: OptionsDataType[],
  key: string,
): string | undefined => {
  const item = list.find((entry) => entry.Key === key);
  return item?.Value;
};
// 🔹 helper: safely convert to number (or undefined)
export const toNumber = (value: unknown): number | undefined => {
  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

// 🔹 helper: safely convert to number but default to 0
export const toNumberOrZero = (value: unknown): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export const getTaxPayloadFromTaxEntries = (
  taxEntries: TaxConfigureTableData[] | undefined,
) => {
  return taxEntries?.map(
    ({ taxType, taxTypeId, description, percentage, amount, id }) => {
      const taxTypeIdNum = taxType?.id ?? taxTypeId;
      return {
        id,
        taxTypeId: taxTypeIdNum,
        description,
        percentage: percentage ? Number(percentage) : 0,
        amount: amount ? Number(amount) : undefined,
      };
    },
  );
};

export const convertFirstLastAndIdToOptions = (
  data: UserDetails[] | undefined,
): OptionsDataType[] => {
  if (!data) return [];
  return data.map((item) => {
    return {
      value: item.id,
      label: getFullName(item?.firstName, item?.lastName),
    };
  });
};

export const getFormattedAmountWithCurrency = (
  value: number | undefined,
  currencyName?: string,
  currencyFormat?: { locale?: string },
) => {
  if (value && isEmpty(value)) return EMPTY_VALUE_DASH;
  const formattedAmount = `${currencyName} ${getFormattedAmount(value ?? 0, currencyFormat?.locale)}`;
  return formattedAmount;
};

export const getFormattedAmountWithCurrencyFromList = (
  amount: number,
  currencyList: IdNameList[],
  currencyTypeId: string,
): string => {
  const currencyDetails = getCurrencyNameAndLocaleById(
    currencyList,
    currencyTypeId,
  );
  const formattedAmount = `${currencyTypeId} ${getFormattedAmount(amount, currencyDetails.currencyLocale)}`;
  return formattedAmount;
};

export const convertFormDataToLocalizeObj = (data: KeyValue) =>
  Object.entries(data)
    .map(([localeKey, value]) => ({ localeKey, name: value }))
    .filter((locale) => locale.name) as Locale[];

export const convertLocalizeObjToFormValue = (data: Locale[]) =>
  data.reduce<Record<string, string>>((acc, { localeKey, name }) => {
    acc[localeKey] = name;
    return acc;
  }, {});

export const emailValidation = {
  onInput: (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;

    if (target.value.length >= MAX_LENGTH_100) {
      target.value = target.value.slice(0, MAX_LENGTH_100);
    }

    target.value = target.value.replace(REGEX_EMAIL_ACCEPTED_SPECIAL_CHARS, "");
  },
};

export const mapSaleCreditToTableData = (
  saleCredit: SalesCredit[],
  showDash: boolean,
): SalesCreditDetail[] => {
  return saleCredit.map((credit) => ({
    id: credit?.office?.id,
    localClientRelationshipOwnerId: showDash
      ? getValueOrDash(credit.localClientRelationshipOwner?.id)
      : getValueOrBlank(credit.localClientRelationshipOwner?.id),
    clientRelationshipOwner: getFullName(
      credit.localClientRelationshipOwner?.firstName,
      credit.localClientRelationshipOwner?.lastName,
      true,
    ),
    officeName: showDash
      ? getValueOrDash(credit.office?.name)
      : getValueOrBlank(credit.office?.name),
    firstName: showDash
      ? getValueOrDash(credit.localClientRelationshipOwner?.firstName)
      : getValueOrBlank(credit.localClientRelationshipOwner?.firstName),
    lastName: showDash
      ? getValueOrDash(credit.localClientRelationshipOwner?.lastName)
      : getValueOrBlank(credit.localClientRelationshipOwner?.lastName),
    email: showDash
      ? getValueOrDash(credit.localClientRelationshipOwner?.email)
      : getValueOrBlank(credit.localClientRelationshipOwner?.email),
    percentage: isNotEmpty(credit?.percentage) ? credit?.percentage : "0",
  }));
};

export const mapProjectFacilitatorToTableData = (
  data: ProjectFacilitatorTypeAssignment[],
  fetchValueByKeyOfDropdown: Function,
  userCountryDateFormat: string,
): FacilitatorDetails[] => {
  return data.map((item: ProjectFacilitatorTypeAssignment) => ({
    assignmentDate: convertToUserFormat(
      item?.assignedDate,
      userCountryDateFormat,
    ),
    functionName: fetchValueByKeyOfDropdown(
      facilitatorSiteCoreKeys.facilitator,
      facilitatorSiteCoreKeys.functionDropdownValue,
      item.functionName,
    ),
    facilitatorId: item?.faciliator?.id,
    facilitatorName: getFullName(
      item?.faciliator?.firstName,
      item?.faciliator?.lastName,
      true,
    ),
    functionId: item?.functionName,
  }));
};

/**
 * Validates and formats a numeric input string to ensure it meets specific criteria.
 * 
 * This function performs comprehensive validation and formatting on numeric input strings:
 * - Ensures only valid numeric characters (digits and decimal point) are allowed
 * - Prevents invalid number formats like leading zeros (e.g., "0001" becomes "0")
 * - Enforces a maximum total digit limit of 15 (excluding the decimal point)
 * - Restricts decimal places to a maximum of 2 digits
 * - Rounds the final numeric value to 2 decimal places for consistency
 * 
 * @param inputValue - The input string to validate and format. Can contain numbers, 
 *                     decimal points, or other characters that will be filtered out.
 * @param maxDigit - The maximum total number of digits allowed (excluding the decimal point).
 * @returns The validated and formatted numeric string that conforms to the validation rules.
 *          Returns the original value if it doesn't match the basic numeric pattern.
 * 
 * @example
 * // Valid inputs
 * validateAndFormatNumericInput("123.45") // returns "123.45"
 * validateAndFormatNumericInput("0001") // returns "0"
 * validateAndFormatNumericInput("123.456") // returns "123.46" (rounded to 2 decimals)
 * validateAndFormatNumericInput("123456789012345.99") // returns "123456789012345.99"
 * 
 * @example
 * // Inputs that exceed limits
 * validateAndFormatNumericInput("1234567890123456789") // returns "123456789012345" (15 digits max)
 * validateAndFormatNumericInput("123.12345") // returns "123.12" (2 decimal places max)
 
 */
export const validateAndFormatNumericInput = (
  inputValue: string,
  maxDigit: number,
): string => {
  let value = inputValue;

  // Prevent starting with multiple zeros like 0001
  if (/^0\d+/.test(value)) value = value.replace(/^0+/, "0");

  // Split integer and decimal parts
  const [integerPart, decimalPart = ""] = value.split(".");

  // Restrict total digits (excluding dot) to maxDigit
  const totalDigits = integerPart.length + decimalPart.length;
  if (totalDigits > maxDigit) {
    const allowedIntegerLength = Math.min(integerPart.length, maxDigit);
    const allowedDecimalLength = Math.max(0, maxDigit - allowedIntegerLength);

    const trimmedInteger = integerPart.slice(0, allowedIntegerLength);
    const trimmedDecimal = decimalPart.slice(0, allowedDecimalLength);
    value = trimmedDecimal
      ? `${trimmedInteger}.${trimmedDecimal}`
      : trimmedInteger;
  }

  // Restrict to 2 decimal places
  if (decimalPart.length > 2) {
    value = `${integerPart}.${decimalPart.slice(0, 2)}`;
  }

  return value;
};

export const setGroupedApiDataToDropDownOption = (
  apiResponse: any,
  keyToGroupMap: Record<string, string> = {},
): GroupedOption[] => {
  if (!apiResponse || typeof apiResponse !== "object") return [];

  const options: GroupedOption[] = [];

  for (const key of Object.keys(apiResponse)) {
    const items = apiResponse[key];
    const groupLabel = keyToGroupMap[key];

    if (!Array.isArray(items)) continue;

    for (const item of items) {
      const id = item?.id;
      const name = item?.name;
      options.push({ id, name, groupBy: groupLabel });
    }
  }

  return options;
};

export const handlePasteValidationOnSpecialChars = (
  e: React.ClipboardEvent<HTMLDivElement>,
  formId: string,
  specialCharPattern: RegExp,
  setError: UseFormSetError<FieldValues>,
  errorMessage: string,
  allowPaste: boolean = true,
) => {
  const pasted = e.clipboardData.getData("text");
  const regSpecialCharPattern = specialCharPattern;
  regSpecialCharPattern.lastIndex = 0;
  if (regSpecialCharPattern.test(pasted)) {
    if (!allowPaste) e.preventDefault();
    setError(formId, {
      type: "custom",
      message: errorMessage,
    });
  }
};

export const getOperatorFilterObject = (
  filterValue: string | null,
  operatorValue: string,
): OperatorFilter => {
  return {
    filter: filterValue,
    operatorId: operatorValue,
  };
};

export const getCountryNameByCode = (
  countryCode: string,
  countryListData: { key: string; title: string }[],
) => {
  if (countryCode.includes(".")) {
    return (
      countryListData?.find(
        (item: { key: string; title: string }) => item.key === countryCode,
      )?.title ?? countryCode
    );
  } else {
    return countryCode;
  }
};

export const contractIdNameFormat = (
  contractId: string = "",
  contractName: string = "",
): string => {
  return `${contractId} [${contractName}]`;
};

export const getContractIdName = (
  contractDocument?: ContractDocument,
): string => {
  if (isEmpty(contractDocument) || isEmpty(contractDocument?.id)) {
    return EMPTY_VALUE_DASH;
  }

  return contractIdNameFormat(
    contractDocument?.contractId,
    contractDocument?.name,
  );
};

export const getServiceOfficeCountryCodes = (
  loggedInUser?: LoginUserDetailsType,
) => {
  const countryCodeList = [
    ...new Set(
      loggedInUser?.role?.serviceOffices?.map((office) => office.countryCode) ??
        [],
    ),
  ];
  const countryCodeListString = countryCodeList.length
    ? countryCodeList.join(",")
    : "";

  return countryCodeListString;
};

export const getCountryCodeWithoutCountryPrefix = (
  countryCode: string = "",
) => {
  return countryCode.replace(COUNTRY_PREFIX, "");
};

export const createNavigationLink = (pageUrl: string, pageId: string) => {
  return `${pageUrl}/${pageId}`;
};

export const getSafeImageUrl = (imageUrl?: string) => {
  if (!imageUrl?.trim()) {
    return undefined;
  }

  try {
    const protocol = new URL(imageUrl).protocol;
    return protocol === "http:" || protocol === "https:" ? imageUrl : undefined;
  } catch {
    return undefined;
  }
};